import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TimezoneHelper } from '@shared/helpers';
import { cloneDeep, isArray } from 'lodash-es';
import { DatePickerModule } from 'primeng/datepicker';
import { FormErrorComponent } from '../form-error/form-error.component';
import { FormLabelComponent } from '../form-label/form-label.component';

@Component({
    selector: 'datepicker-fc',
    standalone: true,
    imports: [TranslateModule, DatePickerModule, ReactiveFormsModule, FormLabelComponent, FormErrorComponent],
    templateUrl: './datepicker-fc.component.html',
    styleUrl: './datepicker-fc.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerFcComponent {
    private translate = inject(TranslateService);

    id = input.required<string>();
    control = input.required<FormControl>();

    label = input<string>('');
    tooltipText = input<string>('');
    required = input<boolean>(false);

    maxDate = input<Date | undefined>(undefined);
    minDate = input<Date | undefined>(undefined);
    maxDateToday = input<boolean>(false); // use if the max date should be today

    placeholder = input<string>('uiKit.form.datepicker.placeholder');
    name = input<string>(''); //used for validation messages, if there is no label

    size = input<'small' | 'large'>('large');

    selectionMode = input<'range' | 'single' | 'multiple'>('range');
    showClear = input<boolean>(true);
    showButtonBar = input<boolean>(true);
    readonlyInput = input<boolean>(true);

    readonly endOfCurrentDay = TimezoneHelper.datePickerEndOfCurrentDate();

    onClose(): void {
        const value = this.control().value;
        if (value && isArray(value) && value[0] !== null && value[1] === null) {
            value[1] = cloneDeep(value[0]);
            this.control().patchValue(value);
        }
    }

    readonly errorMessages: Record<string, (label: string, control: FormControl, error: any) => string> = {
        required: () => `${this.translate.instant('uiKit.form.datepicker-fc.emptyDateFormValidator')}`,
    };

    getErrorMessage(error: string): string {
        const label = this.label() ? this.label() : this.name();

        return this.errorMessages[error]
            ? this.errorMessages[error](label, this.control(), this.control().errors && this.control().errors?.[error])
            : '';
    }

    getErrorKeys(errors: ValidationErrors | null): string[] {
        return errors ? Object.keys(errors) : [];
    }
}
